import { type FC } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';

// MUI
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// screen
import {
  DailyInvoiceModel,
  StructuredDataInfo,
} from 'modules/map/components/daily-consolidate-invoice/types';
import {
  EXTRA_SMALL_FONT_SIZE,
  ORIGINAL_FONT_SIZE,
  renderCell,
  SMALL_FONT_SIZE,
} from 'modules/map/components/pdf-print-form/utils';
import StyledTableHead from 'modules/map/components/pdf-print-form/styled-components/StyledTableHead';
import StyleTableHeadCellText from 'modules/map/components/pdf-print-form/styled-components/StyleTableHeadCellText';
import SizedText from 'modules/map/components/pdf-print-form/styled-components/SizedText';
import PdfFooter from 'modules/map/components/daily-consolidate-invoice/pdf/PdfFooter';

const RightAlignedNumberCell: FC<{
  value: number;
  showDecimal?: boolean;
  bold?: boolean;
  showCurreny?: boolean;
}> = ({ value, showDecimal, bold, showCurreny }) => {
  return (
    <TableCell>
      <Typography
        sx={{
          textAlign: 'right',
          fontWeight: bold ? 600 : 400,
          fontSize: ORIGINAL_FONT_SIZE,
          paddingX: '6px',
        }}
      >
        <FormattedNumber
          value={value}
          style={showCurreny ? 'currency' : undefined}
          currency={showCurreny ? 'VND' : undefined}
          maximumFractionDigits={showDecimal ? 2 : undefined}
          minimumFractionDigits={showDecimal ? 2 : undefined}
        />
      </Typography>
    </TableCell>
  );
};

const LeftAlignedLabelCell: FC<{ intlId: string; colSpan?: number }> = ({
  intlId,
  colSpan = 1,
}) => {
  return (
    <TableCell className="whitespace-pre-wrap" colSpan={colSpan}>
      <Typography
        sx={{
          textAlign: 'left',
          fontWeight: 600,
          fontSize: ORIGINAL_FONT_SIZE,
          paddingX: '6px',
        }}
      >
        <FormattedMessage id={intlId} />
      </Typography>
    </TableCell>
  );
};

const TransparentCell: FC<{ colSpan?: number }> = ({ colSpan = 1 }) => (
  <TableCell
    colSpan={colSpan}
    sx={() => ({
      borderTop: 'none !important',
      borderBottom: 'none !important',
      borderLeft: 'none !important',
    })}
  />
);

const InvoiceTypeOneFooter: FC<{ footerInfo: StructuredDataInfo }> = ({ footerInfo }) => {
  return (
    <Box sx={{ mt: 1, pageBreakInside: 'avoid', breakInside: 'avoid' }}>
      <Typography
        sx={{
          mb: 1,
          fontWeight: 600,
          fontSize: SMALL_FONT_SIZE,
          textAlign: 'end',
        }}
      >
        Hà Nội, ngày.........tháng..........năm {footerInfo.year}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Box sx={{ width: '50%', textAlign: 'center' }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: SMALL_FONT_SIZE,
              mb: 10,
            }}
          >
            Thủ quỹ
          </Typography>
        </Box>
        <Box sx={{ width: '50%', textAlign: 'center' }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: SMALL_FONT_SIZE,
              mb: 10,
            }}
          >
            Bếp trưởng
          </Typography>
        </Box>
        <Box sx={{ width: '50%', textAlign: 'center' }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: SMALL_FONT_SIZE,
              mb: 10,
            }}
          >
            Kế toán
          </Typography>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: SMALL_FONT_SIZE,
            }}
          >
            {footerInfo.menuAccountingOfficer}
          </Typography>
        </Box>
        <Box sx={{ width: '50%', textAlign: 'center' }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: SMALL_FONT_SIZE,
              mb: 10,
            }}
          >
            Giám hiệu
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

interface PdfDailyConsolidateInvoiceTableProp {
  data: DailyInvoiceModel;
}

const PdfDailyConsolidateInvoiceTable: FC<PdfDailyConsolidateInvoiceTableProp> = ({ data }) => {
  const structuredData = data?.structuredData.data;
  const metadata = data?.structuredData.metadata;
  const invoiceData = structuredData.items;
  const isInvoiceTypeOne = metadata?.settlementStatementType === 'offset';

  const invoiceTypeOne = () => (
    <>
      <TableRow>
        <TableCell>
          <SizedText sx={{ fontSize: ORIGINAL_FONT_SIZE }}>A</SizedText>
        </TableCell>
        <TableCell>
          <SizedText
            sx={{
              fontSize: ORIGINAL_FONT_SIZE,
              textAlign: 'left',
              paddingX: '6px',
              fontWeight: 600,
            }}
          >
            Tổng chi trong ngày
          </SizedText>
        </TableCell>
        <TableCell />
        <TableCell />
        <TableCell />
        <RightAlignedNumberCell value={structuredData?.outcome?.totalAmount} bold />
      </TableRow>
      <TableRow>
        <TableCell>
          <SizedText sx={{ fontSize: ORIGINAL_FONT_SIZE }}>B</SizedText>
        </TableCell>
        <TableCell>
          <SizedText
            sx={{
              fontSize: ORIGINAL_FONT_SIZE,
              textAlign: 'left',
              paddingX: '6px',
              fontWeight: 600,
            }}
          >
            Tổng thu trong ngày
            <br />
            (Đã trừ thuế GTGT 8%)
          </SizedText>
        </TableCell>
        <RightAlignedNumberCell value={structuredData?.income?.quantity} bold showDecimal />
        <TableCell />
        <RightAlignedNumberCell value={structuredData?.income?.price} bold />
        <RightAlignedNumberCell value={structuredData?.income?.totalAmount} bold />
      </TableRow>
      <TableRow>
        <TableCell>
          <SizedText sx={{ fontSize: ORIGINAL_FONT_SIZE }}>C</SizedText>
        </TableCell>
        <TableCell>
          <SizedText
            sx={{
              fontSize: ORIGINAL_FONT_SIZE,
              textAlign: 'left',
              paddingX: '6px',
              fontWeight: 600,
            }}
          >
            Mang sang từ hôm trước
          </SizedText>
        </TableCell>
        <TableCell />
        <TableCell />
        <TableCell />
        <RightAlignedNumberCell value={structuredData?.remainYesterday} bold />
      </TableRow>
      <TableRow>
        <TableCell>
          <SizedText sx={{ fontSize: ORIGINAL_FONT_SIZE }}>D</SizedText>
        </TableCell>
        <TableCell>
          <SizedText
            sx={{
              fontSize: ORIGINAL_FONT_SIZE,
              textAlign: 'left',
              paddingX: '6px',
              fontWeight: 600,
            }}
          >
            Chuyển sang ngày hôm sau
            <br />
            (D=B+C-A)
          </SizedText>
        </TableCell>
        <TableCell />
        <TableCell />
        <TableCell />
        <RightAlignedNumberCell value={structuredData?.remainNextDay} bold />
      </TableRow>
    </>
  );

  const invoiceTypeTwo = () => (
    <>
      <TableRow>
        <TransparentCell />
        <LeftAlignedLabelCell intlId="map.dailyOutcome" />
        <RightAlignedNumberCell value={structuredData?.outcome?.quantity} bold showDecimal />
        <TableCell />
        <TableCell />
        <RightAlignedNumberCell value={structuredData?.outcome?.totalAmount} bold showCurreny />
      </TableRow>
      <TableRow>
        <TransparentCell />
        <LeftAlignedLabelCell intlId="map.dailyIncome" />
        <RightAlignedNumberCell value={structuredData?.income?.quantity} bold showDecimal />
        <TableCell />
        <RightAlignedNumberCell value={structuredData?.income?.price} bold />
        <RightAlignedNumberCell value={structuredData?.income?.totalAmount} bold showCurreny />
      </TableRow>
      <TableRow>
        <TransparentCell />
        <LeftAlignedLabelCell intlId="map.tax" />
        <TableCell />
        <TableCell />
        <TableCell />
        <RightAlignedNumberCell value={structuredData?.tax} bold showCurreny />
      </TableRow>
      <TableRow>
        <TransparentCell colSpan={2} />
        <LeftAlignedLabelCell intlId="map.priceAfterTax" colSpan={3} />
        <RightAlignedNumberCell value={structuredData?.incomeAfterTax} bold showCurreny />
      </TableRow>
      <TableRow>
        <TransparentCell colSpan={2} />
        <LeftAlignedLabelCell intlId="map.remainYesterday" colSpan={3} />
        <RightAlignedNumberCell value={structuredData?.remainYesterday} bold showCurreny />
      </TableRow>
      <TableRow>
        <TransparentCell colSpan={2} />
        <LeftAlignedLabelCell intlId="map.totalIncome" colSpan={3} />
        <RightAlignedNumberCell value={structuredData?.total} bold showCurreny />
      </TableRow>
      <TableRow>
        <TransparentCell colSpan={2} />
        <LeftAlignedLabelCell intlId="map.remain" colSpan={3} />
        <RightAlignedNumberCell value={structuredData?.remain} bold showCurreny />
      </TableRow>
    </>
  );

  return (
    <>
      <Typography
        sx={{
          fontWeight: 600,
          textAlign: 'center',
          fontSize: SMALL_FONT_SIZE,
        }}
      >
        BẢNG KÊ QUYẾT TOÁN TRONG NGÀY
      </Typography>
      <Table sx={{ borderCollapse: 'separate' }}>
        <StyledTableHead>
          <TableRow>
            {[
              renderCell({ label: 'stt', width: '7%' }),
              renderCell({ label: 'map.dishName', width: '40%' }),
              renderCell({ label: 'map.quantity', width: '13%' }),
              renderCell({ label: 'map.dvt', width: '10%' }),
              renderCell({ label: 'map.priceBill', width: '12%' }),
              renderCell({ label: 'map.finalPrice', width: '18%' }),
            ].map((item, index) => {
              return (
                <TableCell
                  key={index}
                  className="whitespace-pre-wrap"
                  style={{ width: item.width }}
                >
                  <StyleTableHeadCellText
                    label={item.label}
                    sx={{ fontSize: EXTRA_SMALL_FONT_SIZE, padding: '4px' }}
                  />
                </TableCell>
              );
            })}
          </TableRow>
        </StyledTableHead>
        <TableBody sx={{ '& td': { fontWeight: 700 } }}>
          {invoiceData?.map((value, index) => {
            const shouldDisplayRow = Boolean(value.name);

            return (
              <TableRow key={index} sx={{ '& td': { fontWeight: 400 } }}>
                <TableCell>
                  <SizedText sx={{ fontSize: ORIGINAL_FONT_SIZE }}>{index + 1}</SizedText>
                </TableCell>
                <TableCell>
                  <SizedText
                    sx={{
                      fontSize: ORIGINAL_FONT_SIZE,
                      textAlign: 'left',
                      paddingX: '6px',
                    }}
                  >
                    {shouldDisplayRow ? value.name : ''}
                  </SizedText>
                </TableCell>
                {shouldDisplayRow ? (
                  <RightAlignedNumberCell value={value.quantity} showDecimal />
                ) : (
                  <TableCell />
                )}
                <TableCell>
                  <SizedText
                    sx={{
                      fontSize: ORIGINAL_FONT_SIZE,
                      textAlign: 'center',
                      paddingX: '6px',
                    }}
                  >
                    {shouldDisplayRow ? value.unit : ''}
                  </SizedText>
                </TableCell>
                {shouldDisplayRow ? (
                  <RightAlignedNumberCell value={value.price ?? 0} />
                ) : (
                  <TableCell />
                )}
                {shouldDisplayRow ? (
                  <RightAlignedNumberCell value={value.totalAmount} />
                ) : (
                  <TableCell />
                )}
              </TableRow>
            );
          })}
          {isInvoiceTypeOne ? invoiceTypeOne() : invoiceTypeTwo()}
        </TableBody>
      </Table>
      {isInvoiceTypeOne ? (
        <InvoiceTypeOneFooter footerInfo={structuredData.info} />
      ) : (
        <PdfFooter footerInfo={structuredData.info} />
      )}
    </>
  );
};

export default PdfDailyConsolidateInvoiceTable;
